import {
  addToast,
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from '@heroui/react';
import { useEffect, useRef, useState } from 'react';
import CustomButton from './CustomButton';
import {
  FaCheck,
  FaFacebookF,
  FaLinkedinIn,
  FaWhatsapp,
  FaXTwitter,
} from 'react-icons/fa6';

import { RiLoader2Fill, RiLoader2Line } from 'react-icons/ri';
import { FiDownload } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import { MdOutlineContentCopy } from 'react-icons/md';
import { IoShareSocialOutline } from 'react-icons/io5';
import qrCenterLogo from '../../assets/logo/HirePanther_Icon03.png';

interface QRModalProps {
  name: string;
  isIconOnly: boolean;
}

/** Native share (QR + link) on phones/tablets; desktop social icons open link-only share URLs. */
function isMobileOrTablet(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(pointer: coarse)').matches) return true;
  const iPad =
    /iPad/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (iPad) return true;
  return (
    navigator.maxTouchPoints > 0 &&
    window.matchMedia('(max-width: 1024px)').matches
  );
}

const QRModal = ({ name, isIconOnly }: QRModalProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  /** High-res canvas for download / native share (not shown on screen). */
  const exportCanvasRef = useRef<HTMLCanvasElement>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [pngUrl, setPngUrl] = useState<string>('');
  const [isPngGenerated, setIsPngGenerated] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const QR_SIZE = 200;
  /** Export at this size so download + Web Share match mobile-quality output on desktop. */
  const EXPORT_QR_SIZE = 600;
  const QR_LOGO_SIZE = 42;
  const EXPORT_LOGO_SIZE = Math.round(
    (QR_LOGO_SIZE / QR_SIZE) * EXPORT_QR_SIZE
  );
  const QR_LOGO_SRC = qrCenterLogo;

  useEffect(() => {
    const currentUrl = window.location.href;
    setShareUrl(currentUrl);
  }, []);

  /** Avoid caching a low-res PNG from a race on first open (prod builds often hit this). */
  useEffect(() => {
    if (isOpen) {
      setIsPngGenerated(false);
      setPngUrl('');
    }
  }, [isOpen]);

  // Copy link to clipboard
  const copyToClipboard = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  /**
   * qrcode.react sets bitmap width = size * devicePixelRatio. Wait until the export canvas
   * matches EXPORT_QR_SIZE so we never cache preview-sized output from a timing race.
   */
  const generatePngData = async (): Promise<string | null> => {
    setIsGenerating(true);
    try {
      const dpr = window.devicePixelRatio || 1;
      const minExportBitmap = Math.round(EXPORT_QR_SIZE * dpr);
      const deadline = Date.now() + 4000;

      while (Date.now() < deadline) {
        const el = exportCanvasRef.current;
        if (el && el.width >= minExportBitmap - 2) {
          break;
        }
        await new Promise<void>((r) => requestAnimationFrame(() => r()));
      }

      const exp = exportCanvasRef.current;
      let png: string | null = null;
      let highRes = false;

      if (exp) {
        png = exp.toDataURL('image/png');
        highRes = exp.width >= minExportBitmap - 2;
      } else if (canvasRef.current) {
        png = canvasRef.current.toDataURL('image/png');
        highRes = false;
      }

      if (png) {
        setPngUrl(png);
        setIsPngGenerated(highRes);
      }
      return png;
    } finally {
      setIsGenerating(false);
    }
  };

  // Download QR Image
  const downloadQRWithUrl = (dataUrl: string) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${name}-qr-code.png`;
    link.click();
  };

  const handleDownload = async () => {
    let dataUrl = pngUrl;
    if (!isPngGenerated) {
      const generated = await generatePngData();
      dataUrl = generated || '';
    }
    if (!dataUrl) return;
    downloadQRWithUrl(dataUrl);
  };

  const PROFILE_SHARE_TEXT = 'Check out this trusted business profile!';

  const getQrImageFile = async (): Promise<File | null> => {
    let dataUrl = pngUrl;
    if (!isPngGenerated) {
      const generated = await generatePngData();
      dataUrl = generated || '';
    }
    if (!dataUrl) return null;
    const resp = await fetch(dataUrl);
    const blob = await resp.blob();
    return new File([blob], `${name}-qr-code.png`, {
      type: 'image/png',
    });
  };

  /**
   * Web Share with files is inconsistent: some targets ignore `text` unless `url` is set;
   * others replace the caption with only the link when `text` duplicates the URL.
   * Try a few payloads in order until one succeeds.
   */
  const tryNativeShareQrAndCaption = async (
    file: File
  ): Promise<'shared' | 'aborted' | 'failed'> => {
    if (typeof navigator.share !== 'function') return 'failed';

    const attempts: ShareData[] = [
      {
        title: 'Business Profile QR',
        text: PROFILE_SHARE_TEXT,
        url: shareUrl,
        files: [file],
      },
      {
        title: 'Business Profile QR',
        text: `${PROFILE_SHARE_TEXT}\n\n${shareUrl}`,
        files: [file],
      },
      {
        title: 'Business Profile QR',
        text: `${PROFILE_SHARE_TEXT} ${shareUrl}`,
        url: shareUrl,
        files: [file],
      },
    ];

    for (const shareData of attempts) {
      if (
        typeof navigator.canShare === 'function' &&
        !navigator.canShare(shareData)
      ) {
        continue;
      }
      try {
        await navigator.share(shareData);
        return 'shared';
      } catch (err) {
        const e = err as { name?: string };
        if (e.name === 'AbortError') return 'aborted';
        console.error('Share attempt failed', err);
      }
    }
    return 'failed';
  };

  const openPlatformShareUrl = (
    platform: 'facebook' | 'twitter' | 'whatsapp' | 'linkedin'
  ) => {
    const encodedUrl = encodeURIComponent(shareUrl || '');
    const encodedText = encodeURIComponent(PROFILE_SHARE_TEXT);
    let url = '';

    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
    }

    if (url) {
      window.open(url, '_blank', 'width=700,height=500');
    } else {
      addToast({
        title: 'Cannot Share',
        description: 'Sharing on this platform is not supported via web.',
        radius: 'md',
        color: 'danger',
      });
    }
  };

  // Mobile/tablet: native share (QR + link). Desktop: link-only via platform URLs (original behavior).
  const handleSocialShare = async (
    platform: 'facebook' | 'twitter' | 'whatsapp' | 'linkedin'
  ) => {
    if (!isMobileOrTablet()) {
      openPlatformShareUrl(platform);
      return;
    }
    if (typeof navigator.share === 'function') {
      const file = await getQrImageFile();
      if (file) {
        const result = await tryNativeShareQrAndCaption(file);
        if (result === 'shared' || result === 'aborted') {
          return;
        }
      }
    }
    openPlatformShareUrl(platform);
  };

  return (
    <>
      <div
        onClick={onOpen}
        className={`flex rounded-lg text-white bg-gradient-to-r from-[#0CA7C1] to-[#40E0E3] hover:cursor-pointer select-none max-w-max mx-auto lg:mx-0 p-[3px] items-center ${isIconOnly ? 'px-2' : ''}`}
      >
        <IoShareSocialOutline size={14} className="text-white" />
        <button
          type="button"
          className="ml-1 text-[11px] text-white hover:cursor-pointer hover:brightness-90"
        >
          {isIconOnly ? 'Share Profile' : ' '}
        </button>
      </div>

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalBody>
                <div className="w-full max-w-sm mx-auto flex flex-col items-center gap-6 pb-5">
                  <p className="text-xl text-primary font-semibold text-center pt-4 -mb-3">
                    Share This Profile!
                  </p>
                  <p className="text-caption text-center mb-2">
                    Shareable Link:{' '}
                    <span className="font-mono text-xs truncate w-full block text-gray-700 font-medium">
                      {shareUrl || 'Loading...'}
                    </span>
                  </p>

                  <div className="p-1 bg-white border-2 border-secondary rounded-xl shadow-md relative ">
                    {isGenerating && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl">
                        <RiLoader2Line
                          className="animate-spin text-indigo-600"
                          size={30}
                        />
                      </div>
                    )}
                    <QRCodeCanvas
                      ref={canvasRef}
                      value={shareUrl}
                      size={QR_SIZE}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      marginSize={3}
                      level="H"
                      includeMargin
                      imageSettings={{
                        src: QR_LOGO_SRC,
                        height: QR_LOGO_SIZE,
                        width: QR_LOGO_SIZE,
                        excavate: true,
                      }}
                    />
                    <div className="sr-only pointer-events-none" aria-hidden>
                      <QRCodeCanvas
                        ref={exportCanvasRef}
                        value={shareUrl}
                        size={EXPORT_QR_SIZE}
                        bgColor="#ffffff"
                        fgColor="#000000"
                        marginSize={3}
                        level="H"
                        includeMargin
                        imageSettings={{
                          src: QR_LOGO_SRC,
                          height: EXPORT_LOGO_SIZE,
                          width: EXPORT_LOGO_SIZE,
                          excavate: true,
                        }}
                      />
                    </div>
                  </div>

                  <div className="w-full space-y-3 mt-2">
                    <CustomButton
                      label={
                        isGenerating ? 'Generating...' : 'Download QR Image'
                      }
                      onPress={handleDownload}
                      startContent={
                        isGenerating ? (
                          <RiLoader2Fill className="animate-spin" size={18} />
                        ) : (
                          <FiDownload size={18} />
                        )
                      }
                      disabled={isGenerating}
                      color="secondary"
                      fullWidth
                      size="md"
                    />

                    <CustomButton
                      label={linkCopied ? 'Link Copied!' : 'Copy Profile Link'}
                      onPress={copyToClipboard}
                      startContent={
                        linkCopied ? (
                          <FaCheck size={18} className="text-green-500 " />
                        ) : (
                          <MdOutlineContentCopy size={16} />
                        )
                      }
                      isDisabled={linkCopied}
                      color="default"
                      fullWidth
                      size="md"
                    />
                  </div>

                  <div className="w-full mt-1 border-t pt-3">
                    <p className="text-gray-500 text-xs text-center mb-4">
                      Share Profile
                    </p>
                    <div className="flex justify-center gap-3">
                      <CustomButton
                        isIconOnly
                        onPress={() => handleSocialShare('facebook')}
                        startContent={<FaFacebookF size={20} />}
                        color="default"
                        radius="full"
                        size="md"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      />
                      <CustomButton
                        isIconOnly
                        onPress={() => handleSocialShare('twitter')}
                        startContent={<FaXTwitter size={19} />}
                        color="default"
                        radius="full"
                        size="md"
                        className="bg-black hover:bg-gray-900 text-white"
                      />
                      <CustomButton
                        isIconOnly
                        onPress={() => handleSocialShare('whatsapp')}
                        startContent={<FaWhatsapp size={22} />}
                        color="default"
                        radius="full"
                        size="md"
                        className="bg-green-500 hover:bg-green-600 text-white"
                      />
                      <CustomButton
                        isIconOnly
                        onPress={() => handleSocialShare('linkedin')}
                        startContent={<FaLinkedinIn size={20} />}
                        color="default"
                        radius="full"
                        size="md"
                        className="bg-sky-600 hover:bg-blue-500 text-white"
                      />
                    </div>
                  </div>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default QRModal;
